import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import Slider from "react-slick";
import { all_routes } from "../../router/all_routes";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import { useDispatch } from "react-redux";
import { setCredentials } from "../../../core/redux/authSlice";
import apiInstance from "../../../utility/apiInstance";
type PasswordField = "password" | "confirmPassword";

const Login = () => {

    const loginSLider = {
        dots: true,
        infinite: true,
        slidesToShow: 1,
        slidesToScroll: 1,
        adaptiveHeight: true,
        autoplay: true,
    };

    interface LoginData {
        email: '',
        password: ''
    }

    const [passwordVisibility, setPasswordVisibility] = useState({
        password: false,
        confirmPassword: false,
    });

    const [loginData, setLoginData] = useState<LoginData>({
        email: '',
        password: ''
    })

    const [error, setError] = useState<String | null>(null);
    const [errorFields, setErrorFields] = useState<string[]>([]);
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

    const togglePasswordVisibility = (field: PasswordField) => {
        setPasswordVisibility((prevState) => ({
            ...prevState,
            [field]: !prevState[field],
        }));
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setLoginData((prevState) => ({
            ...prevState,
            [name]: value
        }))
        if (errorFields.includes(name)) {
            setErrorFields((prev) => prev.filter((f) => f !== name));
            setFieldErrors((prev) => {
                const next = { ...prev };
                delete next[name];
                return next;
            });
        }
    }

    const route = all_routes;
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();

    const handleResendAndRedirect = async (email: string) => {
        try {
            await apiInstance.post('/api/users/send-otp', { email });
        } catch {}
        navigate(route.otp, { state: { email } });
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        setError(null);

        if(!loginData.email || !loginData.password) {
            const invalid: string[] = [];
            const messages: Record<string, string> = {};
            if (!loginData.email) {
                invalid.push('email');
                messages.email = 'Email is required.';
            }
            if (!loginData.password) {
                invalid.push('password');
                messages.password = 'Password is required.';
            }
            setErrorFields(invalid);
            setFieldErrors(messages);
            return;
        }
        setErrorFields([]);
        setFieldErrors({});

        try {
            const response = await apiInstance.post('/api/users/login', loginData);

            const { token, user } = response.data;

            if (token) {

                dispatch(setCredentials({token, userId: user?.id}));

                let path = "";
                switch(user?.role) {
                    case "student": 
                        path = route.studentDashboard;
                        break;
                    case "inst_admin":
                        path = route.institutionDashboard;
                        break;
                    case "recruiter":
                        path = route.recruiterDashboard;
                        break;
                    case "account_manager":
                        path = route.recruiterDashboard;
                        break;
                    default:
                        path = route.homesix;
                }
                
                navigate(path);

            } else {
                console.error('No token received from login response');
            }

        } catch (error: any) {
            const message = error?.response?.data?.errors?.[0]?.message || error?.message || "";
            if (message.includes("EMAIL_NOT_VERIFIED")) {
                setError("unverified:" + loginData.email);
            } else {
                setError(message || "Login failed. Please try again.");
            }
            console.error('Login failed:', error);
        }
    };

    return (
        <>
            {/* Main Wrapper */}
            <div className="main-wrapper">
                <div className="login-content">
                    <div className="row">
                        {/* Login Banner */}
                        <div className="col-md-6 login-bg d-none d-lg-flex">
                            <Slider {...loginSLider} className="login-carousel">
                                <div>
                                    <div className="login-carousel-section mb-3">
                                        <div className="login-banner">
                                            <ImageWithBasePath
                                                src="assets/img/auth/auth-1.svg"
                                                className="img-fluid"
                                                alt="Logo"
                                            />
                                        </div>
                                        <div className="mentor-course text-center">
                                            <h3 className="mb-2">
                                                Welcome Back to <br />
                                                Insta<span className="text-secondary">Admissions</span>
                                            </h3>
                                            <p>
                                                Sign in to manage applications, track student progress, and stay connected with your institution — all in one secure dashboard.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <div>
                                    <div className="login-carousel-section mb-3">
                                        <div className="login-banner">
                                            <ImageWithBasePath
                                                src="assets/img/index-3/banner-01.png"
                                                className="img-fluid"
                                                alt="Logo"
                                            />
                                        </div>
                                        <div className="mentor-course text-center">
                                            <h3 className="mb-2">
                                                Your Gateway to <br />
                                                Global Education
                                            </h3>
                                            <p>
                                                Access real-time updates on your applications and communicate directly with institutions and recruiters worldwide.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <div>
                                    <div className="login-carousel-section mb-3">
                                        <div className="login-banner">
                                            <ImageWithBasePath
                                                src="assets/img/index-3/register-img.png"
                                                className="img-fluid"
                                                alt="Logo"
                                            />
                                        </div>
                                        <div className="mentor-course text-center">
                                            <h3 className="mb-2">
                                                Built for Students, <br />
                                                Institutions &amp; Recruiters
                                            </h3>
                                            <p>
                                                Whichever role you play in the admissions journey, InstaAdmissions keeps everyone aligned and informed.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </Slider>
                        </div>
                        {/* /Login Banner */}
                        <div className="col-md-6 login-wrap-bg">
                            {/* Login */}
                            <div className="login-wrapper">
                                <div className="loginbox">
                                    <div className="w-100">
                                        <div className="d-flex align-items-center justify-content-between login-header">
                                            <ImageWithBasePath
                                                src="assets/img/logo-white.png"
                                                className="img-fluid"
                                                alt="Logo"
                                            />
                                            <Link to={route.homesix} className="link-1">
                                                Back to Home
                                            </Link>
                                        </div>
                                        <h1 className="fs-32 fw-bold topic">
                                            Sign into Your Account
                                        </h1>
                                        <form onSubmit={handleSubmit} className="mb-3 pb-3">
                                            <div className="mb-3 position-relative">
                                                <label className="form-label">
                                                    Email<span className="text-danger ms-1">*</span>
                                                </label>
                                                <div className="position-relative">
                                                    <input
                                                        type="email"
                                                        name="email"
                                                        value={loginData.email}
                                                        onChange={handleInputChange}
                                                        className={`form-control form-control-lg ${errorFields.includes('email') ? 'is-invalid' : ''}`}
                                                    />
                                                    <span>
                                                        <i className="isax isax-sms input-icon text-gray-7 fs-14" />
                                                    </span>
                                                </div>
                                                {fieldErrors.email && (
                                                    <div className="invalid-feedback d-block">{fieldErrors.email}</div>
                                                )}
                                            </div>
                                            <div className="mb-3 position-relative">
                                                <label className="form-label">
                                                    Password <span className="text-danger ms-1">*</span>
                                                </label>
                                                <div className="position-relative" id="passwordInput">
                                                    <input
                                                        type={
                                                            passwordVisibility.password ? "text" : "password"
                                                        }
                                                        name="password"
                                                        value={loginData.password}
                                                        onChange={handleInputChange}
                                                        className={`form-control form-control-lg pass-input ${errorFields.includes('password') ? 'is-invalid' : ''}`}
                                                    />
                                                    <span
                                                        className={`isax toggle-passwords fs-14 ${passwordVisibility.password
                                                            ? "isax-eye"
                                                            : "isax-eye-slash"
                                                            }`}
                                                        onClick={() => togglePasswordVisibility("password")}
                                                    ></span>
                                                </div>
                                                {fieldErrors.password && (
                                                    <div className="invalid-feedback d-block">{fieldErrors.password}</div>
                                                )}
                                            </div>
                                            <div className="d-flex align-items-center justify-content-between mb-4">
                                                {/* <div className="remember-me d-flex align-items-center">
                                                    <input
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        defaultValue=""
                                                        id="flexCheckDefault"
                                                    />
                                                    <label
                                                        className="form-check-label ms-2"
                                                        htmlFor="flexCheckDefault"
                                                    >
                                                        Remember Me
                                                    </label>
                                                </div> */}
                                                <div className="">
                                                    <Link to={route.forgotpassword} className="link-2">
                                                        Forgot Password ?
                                                    </Link>
                                                </div>
                                            </div>
                                            <div className="d-grid">
                                                <button
                                                    className="btn btn-secondary btn-lg"
                                                    type="submit"
                                                >
                                                    Login <i className="isax isax-arrow-right-3 ms-1" />
                                                </button>
                                            </div>
                                            <div className="mt-3 text-red fs-14 fw-normal d-flex align-items-center justify-content-center">
                                                {error && error.startsWith("unverified:") ? (
                                                    <span>
                                                        Please verify your email.{" "}
                                                        <button
                                                            type="button"
                                                            className="btn btn-link link-2 p-0 fs-14 align-baseline"
                                                            onClick={() => handleResendAndRedirect(error.replace("unverified:", ""))}
                                                        >
                                                            Resend OTP
                                                        </button>
                                                    </span>
                                                ) : (
                                                    error
                                                )}
                                            </div>
                                        </form>
                                        {/* <div className="d-flex align-items-center justify-content-center or fs-14 mb-3">
                                            Or
                                        </div>
                                        <div className="d-flex align-items-center justify-content-center mb-3">
                                            <Link to="#" className="btn btn-light me-2">
                                                <ImageWithBasePath
                                                    src="assets/img/icons/google.svg"
                                                    alt="img"
                                                    className="me-2"
                                                />
                                                Google
                                            </Link>
                                            <Link to="#" className="btn btn-light">
                                                <ImageWithBasePath
                                                    src="assets/img/icons/facebook.svg"
                                                    alt="img"
                                                    className="me-2"
                                                />
                                                Facebook
                                            </Link>
                                        </div> */}
                                        <div className="fs-14 fw-normal d-flex align-items-center justify-content-center">
                                            Don't you have an account?
                                            <Link to={route.register} className="link-2 ms-1">
                                                {" "}
                                                Sign up
                                            </Link>
                                        </div>
                                        {/* /Login */}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {/* /Main Wrapper */}
        </>
    );
};

export default Login;
